/**
 * Learning Center API: quiz attempts (plan docs/plans/lms-plan.md Appendix A
 * "Progress, attempts, completions"; 7.2, R21, 18.11, 18.15; WP-34's routes,
 * WP-40's client).
 *
 *   POST /courses/:id/attempts {assignment_id?} (?force=1)
 *        -> 201 a new attempt, 200 the caller's in-progress attempt RESUMED with
 *           its stored draw (18.15); { attempt, questions, quiz }
 *        409 LMS_SLIDES_INCOMPLETE | LMS_READING_REQUIRED | LMS_ALREADY_CURRENT
 *            (unless ?force=1) | LMS_ATTEMPT_LIMIT { retry_at } | LMS_NOT_PUBLISHED
 *   GET  /attempts/:id          -> the same, plus results once submitted (18.15)
 *   POST /attempts/:id/submit   {answers: {question_id: [option_id]}}
 *        -> { attempt, results, review_sections, can_retry_at, feedback_mode }
 *        409 LMS_ATTEMPT_EXPIRED
 *
 * The drawn questions carry no correct flag (18.11). BOTH feedback modes get
 * the full per-option feedback in the submit answer, with feedback_mode
 * echoed; the player reveals it question by question for after_each and on
 * the results screen for at_end (18.15).
 */
import api from '../../services/api';
import type { FeedbackMode, QuestionKind } from '../content/types';

export type AttemptStatus = 'in_progress' | 'passed' | 'failed' | 'abandoned';

/** A drawn question as the learner sees it: options in the order shown, no flags (18.11). */
export interface AttemptQuestion {
  id: string;
  kind: QuestionKind;
  stem: string;
  options: Array<{ id: string; text: string }>;
  section_id: string;
}

export interface AttemptSummary {
  id: string;
  course_version_id: string;
  expires_at: string;
  status: AttemptStatus;
  submitted_at?: string | null;
  score_pct?: number | null;
  passed?: boolean | null;
}

/** WP-34 adds the quiz facts the player shows beside the draw. */
export interface AttemptQuizInfo {
  pass_mark_pct: number;
  feedback_mode: FeedbackMode;
  question_count: number;
}

export interface StartedAttempt {
  attempt: AttemptSummary;
  questions: AttemptQuestion[];
  quiz?: AttemptQuizInfo;
}

export interface StartAttemptResult extends StartedAttempt {
  /** True when the server answered 200: the in-progress attempt was resumed (18.15). */
  resumed: boolean;
}

/** One drawn question's result (Appendix A), in the order shown. */
export interface QuestionResult {
  question_id: string;
  correct: boolean;
  selected: string[];
  correct_options: string[];
  /** Every option shown, right or wrong, carries its feedback sentence (6.2 rule 5). */
  feedback: Record<string, string | null>;
  explanation: string | null;
  section_id: string;
}

export interface SubmitResult {
  attempt: {
    id?: string;
    status?: AttemptStatus;
    score_pct: number;
    passed: boolean;
    submitted_at: string;
  };
  results: QuestionResult[];
  /** The sections of the missed questions, in the version's order (7.2). */
  review_sections: string[];
  can_retry_at: string | null;
  feedback_mode?: FeedbackMode;
}

/** GET /attempts/:id: results and review_sections are present once submitted. */
export interface AttemptDetail extends StartedAttempt {
  results?: QuestionResult[];
  review_sections?: string[];
}

const enc = encodeURIComponent;

/** POST /api/lms/courses/:id/attempts */
export async function startAttempt(
  courseId: string,
  options: { assignmentId?: string | null; force?: boolean } = {}
): Promise<StartAttemptResult> {
  const body: { assignment_id?: string } = {};
  if (options.assignmentId) body.assignment_id = options.assignmentId;
  const config = options.force ? { params: { force: 1 } } : undefined;
  const response = await api.post<StartedAttempt>(`/api/lms/courses/${enc(courseId)}/attempts`, body, config);
  return { ...response.data, resumed: response.status === 200 };
}

/** GET /api/lms/attempts/:id */
export async function getAttempt(attemptId: string): Promise<AttemptDetail> {
  const response = await api.get<AttemptDetail>(`/api/lms/attempts/${enc(attemptId)}`);
  return response.data;
}

/** POST /api/lms/attempts/:id/submit */
export async function submitAttempt(attemptId: string, answers: Record<string, string[]>): Promise<SubmitResult> {
  const response = await api.post<SubmitResult>(`/api/lms/attempts/${enc(attemptId)}/submit`, { answers });
  return response.data;
}
